/**
 * CustomCampoParametro — o campo de UM parâmetro, escolhido pelo que ele é.
 *
 * A tela de Configurações não conhece parâmetro nenhum por nome: ela recebe uma
 * lista do servidor e, para cada linha, pergunta a este componente que controle
 * abrir. O `st_tipo` escolhe a família e a `st_unidade` escolhe o campo dentro
 * dela — é por isso que parâmetro novo nasce numa migration e aparece na tela
 * sozinho, sem ninguém tocar em `.tsx`.
 *
 * Leva `Custom` porque é nosso e reusável: a tela de Configurações usa hoje, e
 * qualquer outra que precise editar um parâmetro usa amanhã. Regra do hub —
 * `Custom<Família><Conteúdo>`.
 *
 * ⚠️ CAMPO NUMÉRICO SAI DE `CustomCamposNumero`, sempre — regra dura do
 * `CLAUDE.md`. Nunca `<input type="number">`: o valor viria com ponto decimal,
 * a vírgula do teclado brasileiro seria ignorada e o campo aceitaria notação
 * científica sem ninguém pedir.
 *
 * SÓ AVISA A MUDANÇA, não grava. Quem grava é a tela, que sabe do toast e do
 * erro do servidor — este componente não conhece a API.
 */
import {
  CustomCampoNumero,
  CustomCampoNumeroMoeda,
  CustomCampoNumeroPercentual,
} from '@/components/CustomCamposNumero';

export interface Parametro {
  chave: string;
  grupo: string;
  nome: string;
  valor: string;
  tipo: 'texto' | 'inteiro' | 'decimal' | 'booleano' | 'json';
  unidade: string | null;
  valor_padrao: string | null;
  editavel: boolean;
  ordem: number;
  descricao: string | null;
  atualizacao: string | null;
}

/** O sufixo que acompanha o número quando a unidade é de tempo. */
const SUFIXO_DE: Record<string, string> = {
  dias: 'dias',
  horas: 'h',
  minutos: 'min',
  segundos: 's',
};

export function CustomCampoParametro({
  parametro,
  valor,
  aoMudar,
}: {
  parametro: Parametro;
  valor: string;
  aoMudar: (novo: string) => void;
}) {
  const { tipo, unidade, editavel, chave } = parametro;
  const id = `param-${chave.replace(/\./g, '-')}`;

  if (tipo === 'booleano') {
    return (
      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={id}
          checked={valor === '1'}
          disabled={!editavel}
          onChange={(e) => aoMudar(e.target.checked ? '1' : '0')}
        />
        <label className="form-check-label" htmlFor={id}>
          {valor === '1' ? 'Ligado' : 'Desligado'}
        </label>
      </div>
    );
  }

  if (tipo === 'inteiro' || tipo === 'decimal') {
    const casas = tipo === 'inteiro' ? 0 : 2;
    const comum = { valor, onChange: aoMudar, disabled: !editavel, id, largura: '12rem' };

    if (unidade === 'moeda') return <CustomCampoNumeroMoeda {...comum} />;
    if (unidade === 'percentual') return <CustomCampoNumeroPercentual {...comum} />;
    return <CustomCampoNumero {...comum} casas={casas} sufixo={SUFIXO_DE[unidade ?? '']} />;
  }

  if (tipo === 'json') {
    return (
      <textarea
        className="form-control font-monospace"
        id={id}
        rows={4}
        value={valor}
        disabled={!editavel}
        onChange={(e) => aoMudar(e.target.value)}
      />
    );
  }

  return (
    <input
      type="text"
      className="form-control"
      id={id}
      value={valor}
      disabled={!editavel}
      onChange={(e) => aoMudar(e.target.value)}
    />
  );
}
